/**
 * Centralized API Service
 * Handles all API communications with secure key management and error handling
 */

import { apiConfig } from '../../config/apiConfig';
import { validateDashboardCountsResponse, transformDashboardData } from '../../types/apiTypes';

class ApiService {
  constructor() {
    this.baseURL = apiConfig.baseURL;
    this.apiKey = apiConfig.apiKey;
    this.timeout = apiConfig.timeout || 30000; // 30 seconds default per request
    this.maxTotalTimeout = apiConfig.maxTotalTimeout || 60000; // 60 seconds max total (including retries)
    this.retryAttempts = apiConfig.retryAttempts || 2;
    this.debugMode = apiConfig.debugMode || false;
    
    // Always log API key info (first 8 chars only for security) for diagnostics
    const apiKeyPreview = this.apiKey ? `${this.apiKey.substring(0, 8)}...` : 'NOT SET';
    console.log('🔧 ApiService initialized:', {
      baseURL: this.baseURL,
      timeout: this.timeout,
      retryAttempts: this.retryAttempts,
      apiKeyPreview: apiKeyPreview,
      maxTotalTimeout: this.maxTotalTimeout
    });
    
    if (this.debugMode) {
      console.log('🔧 ApiService debug mode enabled');
    }
  }

  /**
   * Get secure headers for API requests
   * @returns {Object} - Headers object
   */
  getHeaders() {
    return {
      'Content-Type': 'application/json',
      'X-API-Key': this.apiKey,
      'Accept': 'application/json'
    };
  }

  /**
   * Build full URL for endpoint
   * @param {string} endpoint - API endpoint
   * @returns {string} - Full URL
   */
  buildUrl(endpoint) {
    // Remove leading slash if present to avoid double slashes
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
    return `${this.baseURL}/${cleanEndpoint}`;
  }

  /**
   * Sleep for specified milliseconds (for retry delays)
   * @param {number} ms - Milliseconds to sleep
   * @returns {Promise} - Promise that resolves after delay
   */
  sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Calculate exponential backoff delay
   * @param {number} attempt - Current attempt number (0-based)
   * @param {Error} error - Optional error object for rate limit handling
   * @returns {number} - Delay in milliseconds
   */
  getRetryDelay(attempt, error = null) {
    // For rate limit errors, use Retry-After header if available
    if (error && error.status === 429 && error.retryAfter) {
      // Convert seconds to milliseconds, add buffer
      return (error.retryAfter + 1) * 1000;
    }
    
    // For rate limit errors without Retry-After, use longer delays
    if (error && error.status === 429) {
      // Start with 5 seconds, increase exponentially
      return Math.min(5000 * Math.pow(2, attempt), 60000); // Max 60 seconds
    }
    
    // Standard exponential backoff for other errors
    return Math.min(1000 * Math.pow(2, attempt), 10000); // Max 10 seconds
  }

  /**
   * Classify error for handling strategy
   * @param {Error|Response} error - Error or response object
   * @returns {Object} - Error classification
   */
  classifyError(error) {
    if (error.name === 'AbortError') {
      return { type: 'timeout', retryable: true, userMessage: 'Request timed out. Please try again.' };
    }

    if (!navigator.onLine) {
      return { type: 'network', retryable: true, userMessage: 'No internet connection. Please check your network.' };
    }

    if (error.status) {
      const status = error.status;
      
      if (status >= 500) {
        return { type: 'server', retryable: true, userMessage: 'Server error. Please try again in a moment.' };
      }
      
      if (status === 429) {
        // Rate limit errors - fail faster, don't retry too many times
        return { 
          type: 'rateLimit', 
          retryable: true, 
          userMessage: 'Too many requests. Please wait a moment and try again.',
          maxRetries: 1 // Only retry once for rate limits to fail faster
        };
      }
      
      if (status === 401 || status === 403) {
        return { type: 'auth', retryable: false, userMessage: 'Authentication failed. Please check your API configuration.' };
      }
      
      if (status === 404) {
        return { type: 'notFound', retryable: false, userMessage: 'API endpoint not found. Please check the configuration.' };
      }
      
      if (status >= 400) {
        return { type: 'client', retryable: false, userMessage: 'Invalid request. Please try again.' };
      }
    }

    return { type: 'unknown', retryable: true, userMessage: 'An unexpected error occurred. Please try again.' };
  }

  /**
   * Make HTTP request with retry logic
   * @param {string} method - HTTP method
   * @param {string} endpoint - API endpoint
   * @param {Object} options - Request options
   * @returns {Promise<Object>} - Response data
   */
  async makeRequest(method, endpoint, options = {}) {
    const url = this.buildUrl(endpoint);
    const { data, params } = options;

    // Add query parameters if provided
    const finalUrl = params ? `${url}?${new URLSearchParams(params)}` : url;

    // Create a timeout for the entire request (including retries)
    const startTime = Date.now();
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => {
        reject(new Error(`Request timeout: exceeded ${this.maxTotalTimeout}ms total time`));
      }, this.maxTotalTimeout);
    });

    const requestConfig = {
      method: method.toUpperCase(),
      headers: this.getHeaders(),
      signal: AbortSignal.timeout(this.timeout)
    };

    // Add body for POST/PUT requests
    if (data && ['POST', 'PUT', 'PATCH'].includes(requestConfig.method)) {
      requestConfig.body = JSON.stringify(data);
    }

    let lastError;
    
    // Wrap the retry loop in a race with the timeout
    const requestPromise = (async () => {
      for (let attempt = 0; attempt <= this.retryAttempts; attempt++) {
      try {
        if (this.debugMode && attempt > 0) {
          console.log(`🔄 API retry attempt ${attempt} for ${method.toUpperCase()} ${endpoint}`);
        }

        if (this.debugMode) {
          console.log(`📡 API Request: ${method.toUpperCase()} ${finalUrl}`);
        }

        const response = await fetch(finalUrl, requestConfig);

        // Always log rate limit headers for diagnostics (even in production)
        const rateLimitRemaining = response.headers.get('X-RateLimit-Remaining');
        const rateLimitLimit = response.headers.get('X-RateLimit-Limit');
        const rateLimitTier = response.headers.get('X-RateLimit-Tier');
        const quotaRemaining = response.headers.get('X-Quota-Remaining');
        
        if (rateLimitRemaining !== null || rateLimitLimit !== null) {
          console.log('📊 Rate Limit Info:', {
            remaining: rateLimitRemaining,
            limit: rateLimitLimit,
            tier: rateLimitTier,
            quotaRemaining: quotaRemaining,
            endpoint: endpoint,
            percentageUsed: rateLimitRemaining && rateLimitLimit 
              ? `${Math.round((1 - rateLimitRemaining / rateLimitLimit) * 100)}%`
              : 'unknown'
          });
        }

        if (this.debugMode) {
          console.log(`📡 API Response: ${response.status} ${response.statusText}`);
        }

        if (!response.ok) {
          const errorData = await response.text();
          const error = new Error(`HTTP ${response.status}: ${response.statusText}`);
          error.status = response.status;
          error.response = response;
          error.data = errorData;
          
          // Extract Retry-After header for rate limit errors
          if (response.status === 429) {
            const retryAfter = response.headers.get('Retry-After');
            if (retryAfter) {
              error.retryAfter = parseInt(retryAfter, 10);
            }
            
            // Log detailed rate limit info for diagnostics
            console.error('🚫 Rate Limit Error Details:', {
              endpoint: endpoint,
              retryAfter: retryAfter,
              rateLimitRemaining: response.headers.get('X-RateLimit-Remaining'),
              rateLimitLimit: response.headers.get('X-RateLimit-Limit'),
              rateLimitTier: response.headers.get('X-RateLimit-Tier'),
              quotaRemaining: response.headers.get('X-Quota-Remaining'),
              resetTime: response.headers.get('X-RateLimit-Reset'),
              errorBody: errorData
            });
            
            // Also try to parse from response body
            try {
              const errorJson = typeof errorData === 'string' ? JSON.parse(errorData) : errorData;
              if (errorJson.retryAfter) {
                error.retryAfter = errorJson.retryAfter;
              }
              console.error('🚫 Rate Limit Error Body:', errorJson);
            } catch (e) {
              // Not JSON, ignore
            }
          }
          
          throw error;
        }

        const responseData = await response.json();
        
        if (this.debugMode) {
          console.log('✅ API Response data:', responseData);
        }

        return responseData;

      } catch (error) {
        lastError = error;
        const errorInfo = this.classifyError(error);

        if (this.debugMode) {
          console.error(`❌ API Error (attempt ${attempt + 1}):`, {
            type: errorInfo.type,
            retryable: errorInfo.retryable,
            message: error.message
          });
        }

        // For rate limit errors, use the configured maxRetries (which is now 1)
        const maxAttempts = errorInfo.maxRetries !== undefined
          ? errorInfo.maxRetries
          : this.retryAttempts;
        
        // For rate limit errors on first attempt, wait briefly then fail (don't retry)
        if (error.status === 429 && attempt === 0) {
          // Wait a bit to respect rate limit, but fail quickly (max 3 seconds)
          const delay = error.retryAfter ? Math.min(error.retryAfter * 1000, 3000) : 1000;
          if (this.debugMode) {
            console.log(`⏸️ Rate limited - waiting ${delay}ms before failing...`);
          }
          await this.sleep(delay);
          break; // Fail after brief wait, don't retry
        }
        
        // Don't retry if error is not retryable or we've exhausted attempts
        if (!errorInfo.retryable || attempt >= maxAttempts) {
          break;
        }

        // Wait before retry with exponential backoff
        if (attempt < maxAttempts) {
          const delay = this.getRetryDelay(attempt, lastError);
          if (this.debugMode) {
            console.log(`⏳ Waiting ${delay}ms (${(delay / 1000).toFixed(1)}s) before retry...`);
            if (lastError.status === 429 && lastError.retryAfter) {
              console.log(`📋 Rate limit: Retry-After header indicates ${lastError.retryAfter}s`);
            }
          }
          await this.sleep(delay);
        }
        }
      }

      // All retries failed, throw the last error
      const errorInfo = this.classifyError(lastError);
      const apiError = new Error(errorInfo.userMessage);
      apiError.originalError = lastError;
      apiError.type = errorInfo.type;
      throw apiError;
    })();

    // Race the request against the timeout
    try {
      return await Promise.race([requestPromise, timeoutPromise]);
    } catch (error) {
      // If it's a timeout error, classify it properly
      if (error.message.includes('Request timeout')) {
        const timeoutError = new Error('Request took too long. Please try again.');
        timeoutError.type = 'timeout';
        timeoutError.status = 408;
        throw timeoutError;
      }
      throw error;
    }
  }

  /**
   * GET request
   * @param {string} endpoint - API endpoint
   * @param {Object} params - Query parameters
   * @returns {Promise<Object>} - Response data
   */
  async get(endpoint, params = {}) {
    return this.makeRequest('GET', endpoint, { params });
  }

  /**
   * POST request
   * @param {string} endpoint - API endpoint
   * @param {Object} data - Request body data
   * @returns {Promise<Object>} - Response data
   */
  async post(endpoint, data = {}) {
    return this.makeRequest('POST', endpoint, { data });
  }

  /**
   * PUT request
   * @param {string} endpoint - API endpoint
   * @param {Object} data - Request body data
   * @returns {Promise<Object>} - Response data
   */
  async put(endpoint, data = {}) {
    return this.makeRequest('PUT', endpoint, { data });
  }

  /**
   * DELETE request
   * @param {string} endpoint - API endpoint
   * @returns {Promise<Object>} - Response data
   */
  async delete(endpoint) {
    return this.makeRequest('DELETE', endpoint);
  }

  /**
   * Get dashboard counts from API (transformed for home page)
   * @returns {Promise<Object>} - Transformed dashboard data
   */
  async getDashboardCounts() {
    try {
      console.log('📊 Fetching dashboard counts from API...');
      
      const response = await this.get(apiConfig.endpoints.dashboardCounts);
      
      // Validate response structure
      if (!validateDashboardCountsResponse(response)) {
        throw new Error('Invalid dashboard response format');
      }

      // Transform to UI-expected format
      const transformedData = transformDashboardData(response);
      
      console.log('✅ Dashboard counts fetched successfully:', transformedData);
      return transformedData;

    } catch (error) {
      console.error('❌ Failed to fetch dashboard counts:', error.message);
      throw error;
    }
  }

  /**
   * Get raw dashboard counts (full response for Film Tracking)
   * @returns {Promise<Object>} - Full dashboard API response
   */
  async getRawDashboardCounts() {
    try {
      console.log('📊 Fetching raw dashboard counts from API...');
      
      const response = await this.get(apiConfig.endpoints.dashboardCounts);
      
      if (!response.success) {
        throw new Error('Dashboard API request failed');
      }

      console.log('✅ Raw dashboard counts fetched successfully');
      return response;  // Return full response, not transformed

    } catch (error) {
      console.error('❌ Failed to fetch raw dashboard counts:', error.message);
      throw error;
    }
  }

  /**
   * Get all tracked films
   * @param {Object} params - Query parameters (limit, offset, search, etc.)
   * @returns {Promise<Object>} - Films data
   */
  async getFilms(params = {}) {
    try {
      console.log('📊 Fetching all tracked films from API...', { params });
      
      const response = await this.get('/api/v1/films', params);
      
      // Log full response structure for debugging
      console.log('🔍 Full /api/v1/films response:', {
        success: response?.success,
        hasData: 'data' in response,
        dataType: Array.isArray(response?.data) ? 'array' : typeof response?.data,
        dataLength: Array.isArray(response?.data) ? response.data.length : 'N/A',
        hasFilms: 'films' in response,
        filmsType: Array.isArray(response?.films) ? 'array' : typeof response?.films,
        filmsLength: Array.isArray(response?.films) ? response.films.length : 'N/A',
        pagination: response?.pagination,
        responseKeys: Object.keys(response || {}),
        firstDataItem: response?.data?.[0] || response?.films?.[0] || 'N/A'
      });
      
      if (!response.success) {
        throw new Error('API request was not successful');
      }
      
      // Handle different response structures
      const films = Array.isArray(response.data) 
        ? response.data 
        : Array.isArray(response.films)
        ? response.films
        : Array.isArray(response.data?.films)
        ? response.data.films
        : [];

      console.log('✅ Films fetched successfully:', {
        count: films.length,
        total: response.pagination?.total || response.data?.pagination?.total || films.length
      });
      
      // Return response with normalized data structure
      return {
        ...response,
        data: films,
        pagination: response.pagination || response.data?.pagination
      };
    } catch (error) {
      console.error('❌ Failed to fetch films:', error.message);
      throw error;
    }
  }

  /**
   * Get film tracking summary data with filtering options
   * @param {Object} params - Filter parameters
   * @returns {Promise<Object>} - Film tracking data
   */
  async getFilmTrackingSummary(params = {}) {
    try {
      console.log('📊 Fetching film tracking summary from API...', { params });
      
      const response = await this.get('/api/v1/analytics/film-tracking-summary-most-recent', params);
      
      // Validate response structure
      if (!response.success || !response.data) {
        throw new Error('Invalid film tracking response format');
      }

      console.log('✅ Film tracking summary fetched successfully:', {
        filmsCount: response.data.length,
        totalFilms: response.pagination?.total || 0,
        dateRange: response.metadata?.release_date_range
      });
      
      return response;

    } catch (error) {
      // Log full error details for debugging
      console.error('❌ Failed to fetch film tracking summary:', {
        message: error.message,
        status: error.status,
        data: error.data,
        originalError: error.originalError,
        params
      });
      
      // Try to parse error data if it's JSON
      let errorMessage = error.message;
      if (error.data) {
        try {
          const errorJson = typeof error.data === 'string' ? JSON.parse(error.data) : error.data;
          if (errorJson.error || errorJson.message) {
            errorMessage = errorJson.error || errorJson.message;
            console.error('📋 API Error Details:', errorJson);
          }
        } catch (e) {
          // Not JSON, use as-is
          console.error('📋 API Error Response (text):', error.data);
        }
      }
      
      const enhancedError = new Error(errorMessage);
      enhancedError.status = error.status;
      enhancedError.originalError = error;
      throw enhancedError;
    }
  }

  /**
   * Get film survey toplines data for a specific film
   * @param {string} imdbId - IMDB ID of the film
   * @param {Object} filters - Optional filters for demographic/behavioral data
   * @returns {Promise<Object>} - Film survey data across all waves
   */
  async getFilmSurveyToplines(imdbId, filters = {}) {
    try {
      console.log('📊 Fetching film survey toplines for:', imdbId);
      
      // Group by survey date and film recency order to get unique survey waves
      // FilmRecencyOrder in group_by ensures we get it back from the API (1=most recent)
      // title_text included so it's returned in the aggregated response
      let groupByFields = ['imdb_id', 'title_text', 'SurveyCompleteDate', 'FilmRecencyOrder'];
      
      // If demographic/behavioral filters are provided, add them to group_by
      // to ensure we get one row per unique combination
      const demographicFields = ['movie_frequency_category', 'agegroup', 'gender', 'ethnicity', 'income', 'identity'];
      const appliedDemographicFilters = demographicFields.filter(field => filters[field]);
      
      if (appliedDemographicFilters.length > 0) {
        // If there are demographic filters, include them in group_by
        groupByFields = [...groupByFields, ...appliedDemographicFilters];
        console.log('📊 Including demographic fields in group_by:', appliedDemographicFilters);
      }
      
      const params = {
        aggregate: 'true',
        group_by: groupByFields.join(','),
        imdb_id: imdbId,
        ...filters
      };
      
      const response = await this.get('/api/v1/analytics/app-film-survey-toplines', params);
      
      if (!response.success || !response.data) {
        throw new Error('Invalid film survey response format');
      }

      console.log('✅ Film survey toplines fetched successfully:', {
        imdbId,
        wavesCount: response.data.length,
        filters,
        groupBy: groupByFields.join(','),
        firstWave: response.data[0]
      });
      
      return response;

    } catch (error) {
      console.error('❌ Failed to fetch film survey toplines:', error.message);
      throw error;
    }
  }

  /**
   * Get aggregated film survey toplines for deep dive analysis
   * @param {Object} filters - Aggregation filters including group_by
   * @returns {Promise} Survey data grouped by specified dimensions
   */
  async getFilmSurveyToplinesAggregated(filters = {}) {
    try {
      console.log('🔬 Fetching aggregated film survey data:', filters);
      
      const params = {
        aggregate: 'true',
        ...filters
      };
      
      const response = await this.get('/api/v1/analytics/app-film-survey-toplines', params);
      
      if (!response.success || !response.data) {
        throw new Error('Invalid aggregated survey response format');
      }

      console.log('✅ Aggregated film survey data fetched successfully:', {
        recordCount: response.data.length,
        filters
      });
      
      return response;
    } catch (error) {
      console.error('❌ Failed to fetch aggregated film survey data:', error.message);
      
      // Preserve error type and status for better error handling upstream
      const enhancedError = new Error(error.message || 'Failed to fetch aggregated film survey data');
      enhancedError.type = error.type;
      enhancedError.status = error.status;
      enhancedError.originalError = error;
      throw enhancedError;
    }
  }

  /**
   * Get conversion funnel analysis with demographic cohort tracking
   * @param {string} imdbId - IMDB ID of the film
   * @param {Object} options - Analysis options
   * @param {Array<string>} options.demographics - Array of demographic types to analyze (agegroup, gender, ethnicity, movie_frequency_category)
   * @param {number} options.minSampleSize - Minimum responses per segment (default: 30)
   * @param {number} options.filmRecencyOrder - Filter by survey wave (optional)
   * @param {string} options.format - Response format: 'json' or 'csv' (default: 'json')
   * @returns {Promise<Object>} - Structured funnel data with cohort analysis
   */
  async getConversionFunnelAnalysis(imdbId, options = {}) {
    try {
      console.log('🔄 Fetching conversion funnel analysis for:', imdbId, options);
      
      // Build query string manually to support multiple demographics parameters
      const queryParams = new URLSearchParams();
      queryParams.append('imdb_id', imdbId);
      
      // Add demographics as multiple separate query params
      if (options.demographics && Array.isArray(options.demographics)) {
        options.demographics.forEach(demo => {
          queryParams.append('demographics', demo);
        });
      }
      
      // Add optional parameters
      if (options.minSampleSize !== undefined) {
        queryParams.append('min_sample_size', options.minSampleSize);
      }
      
      if (options.filmRecencyOrder !== undefined) {
        queryParams.append('film_recency_order', options.filmRecencyOrder);
      }
      
      if (options.format) {
        queryParams.append('format', options.format);
      }
      
      // Build full URL with query string
      const url = `${this.buildUrl('/api/v1/analytics/conversion-funnel-analysis')}?${queryParams.toString()}`;
      
      console.log('📡 Funnel API URL:', url);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: this.getHeaders(),
        signal: AbortSignal.timeout(this.timeout)
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: response.statusText }));
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      
      if (!data.success || !data.data) {
        throw new Error('Invalid conversion funnel response format');
      }

      console.log('✅ Conversion funnel analysis fetched successfully:', {
        imdbId,
        film: data.data.film?.title,
        totalResponses: data.data.film?.total_responses,
        demographicsAnalyzed: options.demographics?.length || 0
      });
      
      return data;
    } catch (error) {
      console.error('❌ Failed to fetch conversion funnel analysis:', error.message);
      throw error;
    }
  }

  /**
   * Get IMDB metadata for a specific film
   * @param {string} imdbId - IMDB ID of the film
   * @returns {Promise<Object>} - IMDB film metadata
   */
  async getIMDBMetadata(imdbId) {
    try {
      console.log('🎬 Fetching IMDB metadata for:', imdbId);
      
      const response = await this.get(`/api/v1/imdb/${imdbId}`);
      
      if (!response.success || !response.data) {
        throw new Error('Invalid IMDB metadata response format');
      }

      console.log('✅ IMDB metadata fetched successfully:', {
        imdbId,
        title: response.data.movie?.title_text
      });
      
      return response;

    } catch (error) {
      console.error('❌ Failed to fetch IMDB metadata:', error.message);
      throw error;
    }
  }

  /**
   * Create a new box office projection
   * @param {Object} projectionData - Projection data
   * @returns {Promise<Object>} - Created projection
   */
  async createBoxOfficeProjection(projectionData) {
    try {
      console.log('📊 Creating box office projection:', projectionData);
      console.log('📊 Data types:', {
        filmId: typeof projectionData.filmId,
        projectionDate: typeof projectionData.projectionDate,
        outlierMinimum: typeof projectionData.outlierMinimum,
        actualProjection: typeof projectionData.actualProjection,
        outlierCeiling: typeof projectionData.outlierCeiling,
        notes: typeof projectionData.notes,
        createdBy: typeof projectionData.createdBy
      });
      
      const response = await this.post('/api/v1/box-office-projections', projectionData);
      
      if (!response.success || !response.data) {
        throw new Error('Invalid box office projection response format');
      }

      console.log('✅ Box office projection created successfully:', {
        id: response.data.id,
        filmId: response.data.filmId,
        actualProjection: response.data.actualProjection
      });
      
      return response;

    } catch (error) {
      console.error('❌ Failed to create box office projection:', error.message);
      throw error;
    }
  }

  /**
   * Get box office projections with filtering and pagination
   * @param {Object} params - Query parameters
   * @returns {Promise<Object>} - Projections data
   */
  async getBoxOfficeProjections(params = {}) {
    try {
      console.log('📊 Fetching box office projections:', params);
      
      const response = await this.get('/api/v1/box-office-projections', params);
      
      if (!response.success || !response.data) {
        throw new Error('Invalid box office projections response format');
      }

      console.log('✅ Box office projections fetched successfully:', {
        total: response.pagination?.total || 0,
        count: response.data?.length || 0
      });
      
      return response;

    } catch (error) {
      console.error('❌ Failed to fetch box office projections:', error.message);
      throw error;
    }
  }

  /**
   * Get a specific box office projection by ID
   * @param {string} projectionId - Projection ID
   * @returns {Promise<Object>} - Projection data
   */
  async getBoxOfficeProjection(projectionId) {
    try {
      console.log('📊 Fetching box office projection:', projectionId);
      
      const response = await this.get(`/api/v1/box-office-projections/${projectionId}`);
      
      if (!response.success || !response.data) {
        throw new Error('Invalid box office projection response format');
      }

      console.log('✅ Box office projection fetched successfully:', {
        id: response.data.id,
        filmId: response.data.filmId
      });
      
      return response;

    } catch (error) {
      console.error('❌ Failed to fetch box office projection:', error.message);
      throw error;
    }
  }

  /**
   * Get all projections for a specific film
   * @param {string} filmId - Film ID
   * @param {Object} params - Query parameters
   * @returns {Promise<Object>} - Film projections data
   */
  async getFilmBoxOfficeProjections(filmId, params = {}) {
    try {
      console.log('📊 Fetching box office projections for film:', filmId);
      
      const response = await this.get(`/api/v1/box-office-projections/films/${filmId}`, params);
      
      if (!response.success || !response.data) {
        throw new Error('Invalid film box office projections response format');
      }

      console.log('✅ Film box office projections fetched successfully:', {
        filmId: response.data.filmId,
        totalProjections: response.data.totalProjections
      });
      
      return response;

    } catch (error) {
      console.error('❌ Failed to fetch film box office projections:', error.message);
      throw error;
    }
  }

  /**
   * Update an existing box office projection
   * @param {string} projectionId - Projection ID
   * @param {Object} updates - Updates to apply
   * @returns {Promise<Object>} - Updated projection
   */
  async updateBoxOfficeProjection(projectionId, updates) {
    try {
      console.log('📊 Updating box office projection:', projectionId, updates);
      
      const response = await this.put(`/api/v1/box-office-projections/${projectionId}`, updates);
      
      if (!response.success || !response.data) {
        throw new Error('Invalid box office projection update response format');
      }

      console.log('✅ Box office projection updated successfully:', {
        id: response.data.id,
        updatedFields: response.metadata?.updatedFields
      });
      
      return response;

    } catch (error) {
      console.error('❌ Failed to update box office projection:', error.message);
      throw error;
    }
  }

  /**
   * Delete a box office projection
   * @param {string} projectionId - Projection ID
   * @returns {Promise<Object>} - Delete result
   */
  async deleteBoxOfficeProjection(projectionId) {
    try {
      console.log('📊 Deleting box office projection:', projectionId);
      
      const response = await this.delete(`/api/v1/box-office-projections/${projectionId}`);
      
      if (!response.success) {
        throw new Error('Invalid box office projection delete response format');
      }

      console.log('✅ Box office projection deleted successfully:', {
        id: projectionId
      });
      
      return response;

    } catch (error) {
      console.error('❌ Failed to delete box office projection:', error.message);
      throw error;
    }
  }

  /**
   * Health check endpoint
   * @returns {Promise<Object>} - Health status
   */
  async healthCheck() {
    try {
      const response = await this.get(apiConfig.endpoints.health);
      return response;
    } catch (error) {
      console.error('❌ Health check failed:', error.message);
      throw error;
    }
  }
}

// Create and export singleton instance
const apiService = new ApiService();

export default apiService;
export { ApiService };
